import React from 'react';
import { Link } from 'react-router-dom';
import {
  CircleStackIcon,
  TableCellsIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { Badge, Button, buttonVariants } from '../ui';
import { cn } from '../../shared/lib/cn';
import {
  useGetConnectorRegistry,
  useSyncRegistry,
} from '../../hooks/useWarehouseRegistry';

export interface WarehouseRegistrySectionProps {
  connectorId: string | number;
  connectorDisplayName: string;
  warehouseKindLabel: string;
  projectHint?: string | null;
}

export function WarehouseRegistrySection({
  connectorId,
  connectorDisplayName,
  warehouseKindLabel,
}: WarehouseRegistrySectionProps) {
  const { data: projects, isLoading } = useGetConnectorRegistry(connectorId);
  const syncRegistry = useSyncRegistry(connectorId);

  const handleSync = () => {
    syncRegistry.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-300 dark:border-dark-border">
        <ArrowPathIcon className="h-6 w-6 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Loading asset registry...</span>
      </div>
    );
  }

  // Flatten the hierarchy for the table view
  const allTables = projects?.flatMap(p =>
    p.datasets.flatMap(d =>
      d.tables.map(t => ({
        ...t,
        datasetName: d.name || d.dataset_external_id,
        projectName: p.name || p.project_external_id,
        rowKey: `${p.id}-${d.id}-${t.id}`
      }))
    )
  ) || [];

  return (
    <section className="space-y-4" data-testid="warehouse-registry-section">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
            <TableCellsIcon className="h-3.5 w-3.5" />
            Data asset registry (Phase 3)
          </div>
          <h3 className="mt-1 text-sm font-medium text-charcoal dark:text-gray-100">
            Physical warehouse objects for {warehouseKindLabel}
          </h3>
          <p className="mt-1 max-w-3xl text-xs leading-5 text-gray-500 dark:text-gray-400">
            Registry assets are <span className="font-semibold text-charcoal dark:text-gray-200">physical data objects</span>.
            Syncing the registry allows Eliza to understand the warehouse structure without requiring manual schema entry.
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/catalog/assets"
            className={buttonVariants({ variant: 'secondary', size: 'sm', className: 'gap-2' })}
          >
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
            Manage in Catalog
          </Link>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={handleSync}
            disabled={syncRegistry.isPending}
          >
            <ArrowPathIcon className={cn("h-3.5 w-3.5", syncRegistry.isPending && "animate-spin")} />
            Sync Metadata
          </Button>
          <Badge variant="secondary">{connectorDisplayName}</Badge>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
          <div className="flex items-center gap-2 text-eliza-red">
            <CircleStackIcon className="h-5 w-5" />
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Projects
            </span>
          </div>
          <p className="mt-2 text-lg font-semibold text-charcoal dark:text-gray-100">
            {projects?.length || 0}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Datasets Tracked</p>
          <p className="mt-2 text-lg font-semibold text-charcoal dark:text-gray-100">
            {projects?.reduce((acc, p) => acc + p.datasets.length, 0) || 0}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Tables / views
          </p>
          <p className="mt-2 text-lg font-semibold text-charcoal dark:text-gray-100">
            {allTables.length}
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-dark-border overflow-hidden">
        <div className="flex flex-col gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-dark-border dark:bg-dark-surface-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">
            Data Connections shows the synced warehouse asset summary. Manage column metadata, descriptions, and corrections in Catalog.
          </p>
          <Link
            to="/catalog/assets"
            className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-eliza-red hover:text-eliza-red-light"
          >
            Manage in Catalog
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Dataset</th>
                <th className="px-4 py-3">Table Name</th>
                <th className="px-4 py-3">Row Count</th>
                <th className="px-4 py-3">Last Synced</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-dark-border dark:bg-dark-surface">
              {allTables.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    No assets synced. Click "Sync Metadata" to begin.
                  </td>
                </tr>
              ) : (
                allTables.map((table) => (
                  <tr key={table.rowKey} className="transition-colors hover:bg-gray-50 dark:hover:bg-dark-surface-2">
                    <td className="px-4 py-3 font-medium text-charcoal dark:text-gray-100">{table.projectName}</td>
                    <td className="px-4 py-3 font-mono text-xs">{table.datasetName}</td>
                    <td className="px-4 py-3 font-medium text-charcoal dark:text-gray-100">{table.name || table.table_external_id}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{table.row_count_snapshot?.toLocaleString() || '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{table.last_synced_at ? new Date(table.last_synced_at).toLocaleDateString() : 'Never'}</td>
                    <td className="px-4 py-3">
                      {table.is_active === false ? (
                        <Badge variant="secondary">Inactive</Badge>
                      ) : (
                        <Badge variant="success">Active</Badge>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
